import { suggestionPopupOpen } from "./suggestion_popup"

// FD-1311: Escape belongs to whatever is on top. A <cs-drawer> (the work item peek, the
// error group peek) closes on Escape from its own keydown listener on document. It can't
// see a native modal <dialog> opened over it (the command palette, the shortcuts help,
// the delete confirmation) or an editor's suggestion popup (FD-621): those aren't
// Cornerstone elements, so they aren't in the list Cornerstone keeps of which open element
// gets Escape. Without this, Escape in any of them closes the drawer and leaves them open.
//
// The capture listener sees the key first, before ProseMirror closes its popup, and only
// takes note. The listener on <html> stops the key on its way back up: after the popup or
// dialog has had it, before it reaches document, where the drawer listens. A dialog still
// closes, because Escape closes a native modal by default and this doesn't cancel the
// default. For a popup it does cancel it, because the drawer's own <dialog> is the one that
// default would close.
//
// Call it when the drawer opens. It returns a function that removes both listeners; call
// that when the drawer closes, so nothing is held back while no drawer is open.
export function yieldDrawerEscape() {
  let noted = null
  const capture = (event) => {
    if (event.key !== "Escape") return
    if (suggestionPopupOpen()) noted = { event, popup: true }
    else if (modalDialogOpen(event.target)) noted = { event, popup: false }
    else noted = null
  }
  const bubble = (event) => {
    if (!noted || noted.event !== event) return
    const { popup } = noted
    noted = null
    event.stopPropagation()
    if (popup) event.preventDefault()
  }
  document.addEventListener("keydown", capture, true)
  document.documentElement.addEventListener("keydown", bubble)
  return () => {
    document.removeEventListener("keydown", capture, true)
    document.documentElement.removeEventListener("keydown", bubble)
  }
}

// A native modal <dialog> is open: the key came from inside one, or one is open anywhere
// (Escape whose target is the body). A <cs-drawer>'s own <dialog> is in its shadow root,
// out of querySelector's reach, so it never counts. :modal rather than [open], so a dialog
// shown non-modally can't take Escape away from the drawer.
function modalDialogOpen(target) {
  return !!(target?.closest?.("dialog:modal") || document.querySelector("dialog:modal"))
}
